import { useState, useMemo } from 'react';
import SectionHeader from '../../components/admin/SectionHeader';
import DataTable from '../../components/admin/DataTable';
import ConfirmDialog from '../../components/admin/ConfirmDialog';

function AdminMigration({ apiFetch, token, toast }) {
  const [migrationPreview, setMigrationPreview] = useState(null);
  const [migrationRunning, setMigrationRunning] = useState(false);
  const [migrationResult, setMigrationResult] = useState(null);
  const [pausingMigration, setPausingMigration] = useState({});
  const [pauseDuration, setPauseDuration] = useState({});
  const [migratingSingle, setMigratingSingle] = useState({});
  const [sendNotifyEmails, setSendNotifyEmails] = useState(false);
  const [notifyPreview, setNotifyPreview] = useState(null);
  const [notifyRunning, setNotifyRunning] = useState(false);
  const [notifyResult, setNotifyResult] = useState(null);
  const [confirmMigrate, setConfirmMigrate] = useState(false);
  const [confirmNotify, setConfirmNotify] = useState(false);

  // Filters
  const [serviceFilter, setServiceFilter] = useState('all');
  const [planFilter, setPlanFilter] = useState('all');

  // Row selection
  const [selectedRows, setSelectedRows] = useState(new Set());

  // Per-row email overrides
  const [rowSendEmail, setRowSendEmail] = useState({});

  // Migrate selected
  const [migratingSelected, setMigratingSelected] = useState(false);

  // Undo / history tab
  const [activeTab, setActiveTab] = useState('migrate');
  const [migrationSessions, setMigrationSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [expandedSession, setExpandedSession] = useState(null);
  const [undoingIds, setUndoingIds] = useState(new Set());

  // Wave migration
  const [waveFilter, setWaveFilter] = useState('all'); // 'all' | 'recent' | 'previous'
  const [graceUntilDate, setGraceUntilDate] = useState(''); // YYYY-MM-DD

  // Grouping
  const [groupByPrice, setGroupByPrice] = useState(true);
  const [collapsedGroups, setCollapsedGroups] = useState(new Set());

  // ── Derived data ────────────────────────────────────────────────────────────

  const rows = (migrationResult || migrationPreview)?.results || [];
  const isDone = !!migrationResult;
  const pausedRows = (migrationResult || migrationPreview)?.pausedResults || [];

  const recentCount = useMemo(() => rows.filter(r => r.wave === 'recent').length, [rows]);
  const previousCount = useMemo(() => rows.filter(r => r.wave === 'previous').length, [rows]);
  const hasWaves = recentCount > 0 && previousCount > 0;

  const availableServices = useMemo(() =>
    ['all', ...new Set(rows.map(r => r.serviceKey).filter(Boolean))], [rows]);

  const availablePlans = useMemo(() => {
    const base = serviceFilter === 'all' ? rows : rows.filter(r => r.serviceKey === serviceFilter);
    return ['all', ...new Set(base.map(r => r.planSlug))];
  }, [rows, serviceFilter]);

  const filteredRows = useMemo(() => rows.filter(r =>
    (serviceFilter === 'all' || r.serviceKey === serviceFilter) &&
    (planFilter === 'all' || r.planSlug === planFilter) &&
    (waveFilter === 'all' || r.wave === waveFilter)
  ).sort((a, b) => {
    // Primary sort: old price ascending (cheapest/oldest price first)
    if ((a.oldPrice || 0) !== (b.oldPrice || 0)) return (a.oldPrice || 0) - (b.oldPrice || 0);
    // Secondary sort: subscription created date ascending (oldest customer first)
    return (a.subscriptionCreated || 0) - (b.subscriptionCreated || 0);
  }), [rows, serviceFilter, planFilter, waveFilter]);

  // Group filtered rows by oldPrice → newPrice key
  const priceGroups = useMemo(() => {
    if (!groupByPrice) return null;
    const groups = {};
    for (const row of filteredRows) {
      const key = `${row.oldPrice ?? row.oldPriceId}→${row.newPrice ?? row.newPriceId}`;
      if (!groups[key]) groups[key] = { key, oldPrice: row.oldPrice, newPrice: row.newPrice, currency: row.currency, interval: row.interval, rows: [] };
      groups[key].rows.push(row);
    }
    return Object.values(groups);
  }, [filteredRows, groupByPrice]);

  const toggleGroup = (key) => setCollapsedGroups(prev => {
    const s = new Set(prev);
    s.has(key) ? s.delete(key) : s.add(key);
    return s;
  });

  const allFilteredSelected = filteredRows.length > 0 &&
    filteredRows.every(r => selectedRows.has(r.subscriptionId));

  const toggleSelectAll = (e) => {
    setSelectedRows(prev => {
      const s = new Set(prev);
      if (e.target.checked) {
        filteredRows.forEach(r => s.add(r.subscriptionId));
      } else {
        filteredRows.forEach(r => s.delete(r.subscriptionId));
      }
      return s;
    });
  };

  // ── Handlers ────────────────────────────────────────────────────────────────

  const handlePreviewMigration = async () => {
    setMigrationRunning(true);
    setMigrationResult(null);
    try {
      const res = await apiFetch('/api/admin/migrate-prices', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ dryRun: true }) });
      const data = await res.json();
      setMigrationPreview(data);
      // Init per-row email state
      const emailMap = {};
      (data.results || []).forEach(r => { emailMap[r.subscriptionId] = sendNotifyEmails; });
      setRowSendEmail(emailMap);
      setSelectedRows(new Set());
      setServiceFilter('all');
      setPlanFilter('all');
    } catch { setMigrationPreview({ error: 'Request failed' }); }
    finally { setMigrationRunning(false); }
  };

  const handleGlobalEmailChange = (checked) => {
    setSendNotifyEmails(checked);
    const emailMap = {};
    rows.forEach(r => { emailMap[r.subscriptionId] = checked; });
    setRowSendEmail(emailMap);
  };

  const handleRunMigration = async () => {
    setConfirmMigrate(false);
    setMigrationRunning(true);
    setMigrationResult(null);
    try {
      const emailMap = {};
      rows.forEach(r => { emailMap[r.subscriptionId] = rowSendEmail[r.subscriptionId] ?? sendNotifyEmails; });
      const graceUntilTimestamp = graceUntilDate ? Math.floor(new Date(graceUntilDate).getTime() / 1000) : null;
      const res = await apiFetch('/api/admin/migrate-prices', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ dryRun: false, sendNotification: false, rowEmailMap: emailMap, waveFilter, graceUntilTimestamp }) });
      const data = await res.json();
      setMigrationResult(data);
      setMigrationPreview(null);
      const summary = `${data.migrated} updated, ${data.failed} failed${data.gracePaused > 0 ? `, ${data.gracePaused} deferred (grace period)` : ''}`;
      toast.success('Migration complete', summary);
    } catch { setMigrationResult({ error: 'Request failed' }); toast.error('Error', 'Migration request failed'); }
    finally { setMigrationRunning(false); }
  };

  const handleMigrateSingle = async (row) => {
    setMigratingSingle(prev => ({ ...prev, [row.subscriptionId]: true }));
    try {
      const res = await apiFetch('/api/admin/migrate-prices', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          dryRun: false,
          subscriptionId: row.subscriptionId,
          sendNotification: false,
          rowEmailMap: { [row.subscriptionId]: rowSendEmail[row.subscriptionId] ?? sendNotifyEmails }
        })
      });
      const data = await res.json();
      if (data.migrated === 1) {
        toast.success('Migrated', `${row.customerEmail} updated to current price`);
      } else {
        const err = data.results?.[0]?.error || 'Unknown error';
        toast.error('Failed', err);
      }
      // Refresh preview
      const previewRes = await apiFetch('/api/admin/migrate-prices', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ dryRun: true }) });
      const previewData = await previewRes.json();
      setMigrationPreview(previewData);
      setMigrationResult(null);
      const emailMap = {};
      (previewData.results || []).forEach(r => { emailMap[r.subscriptionId] = rowSendEmail[r.subscriptionId] ?? sendNotifyEmails; });
      setRowSendEmail(emailMap);
    } catch { toast.error('Error', 'Migration request failed'); }
    finally { setMigratingSingle(prev => ({ ...prev, [row.subscriptionId]: false })); }
  };

  const handleMigrateSelected = async () => {
    setMigratingSelected(true);
    const ids = [...selectedRows];
    const emailMap = {};
    ids.forEach(id => { emailMap[id] = rowSendEmail[id] ?? sendNotifyEmails; });
    try {
      const res = await apiFetch('/api/admin/migrate-prices', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dryRun: false, subscriptionIds: ids, sendNotification: false, rowEmailMap: emailMap })
      });
      const data = await res.json();
      toast.success('Done', `${data.migrated} migrated, ${data.failed} failed`);
      setSelectedRows(new Set());
      // Refresh preview
      const previewRes = await apiFetch('/api/admin/migrate-prices', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ dryRun: true }) });
      const previewData = await previewRes.json();
      setMigrationPreview(previewData);
      setMigrationResult(null);
      const newEmailMap = {};
      (previewData.results || []).forEach(r => { newEmailMap[r.subscriptionId] = rowSendEmail[r.subscriptionId] ?? sendNotifyEmails; });
      setRowSendEmail(newEmailMap);
    } catch { toast.error('Error', 'Migration failed'); }
    finally { setMigratingSelected(false); }
  };

  const handlePauseMigration = async (subId, months) => {
    setPausingMigration(prev => ({ ...prev, [subId]: true }));
    const pauseUntil = Math.floor(Date.now() / 1000) + months * 30 * 24 * 3600;
    try {
      await apiFetch(`/api/admin/subscriptions/${subId}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ migration_paused_until: pauseUntil }) });
      const res = await apiFetch('/api/admin/migrate-prices', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ dryRun: true }) });
      const data = await res.json();
      setMigrationPreview(data);
      setMigrationResult(null);
      toast.success('Paused', 'Migration paused for this subscription');
    } catch { toast.error('Error', 'Failed to pause migration'); }
    finally { setPausingMigration(prev => ({ ...prev, [subId]: false })); }
  };

  const handleClearMigrationPause = async (subId) => {
    setPausingMigration(prev => ({ ...prev, [subId]: true }));
    try {
      await apiFetch(`/api/admin/subscriptions/${subId}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ migration_paused_until: '' }) });
      const res = await apiFetch('/api/admin/migrate-prices', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ dryRun: true }) });
      const data = await res.json();
      setMigrationPreview(data);
      setMigrationResult(null);
      toast.success('Resumed', 'Migration resumed for this subscription');
    } catch { toast.error('Error', 'Failed to clear pause'); }
    finally { setPausingMigration(prev => ({ ...prev, [subId]: false })); }
  };

  const loadSessions = async () => {
    setSessionsLoading(true);
    try {
      const res = await apiFetch('/api/admin/price-migration-sessions');
      const data = await res.json();
      setMigrationSessions(data.sessions || []);
    } catch { toast.error('Error', 'Failed to load history'); }
    finally { setSessionsLoading(false); }
  };

  const handleUndo = async (logIds) => {
    setUndoingIds(prev => new Set([...prev, ...logIds]));
    try {
      const res = await apiFetch('/api/admin/price-migration-undo', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ logIds })
      });
      const data = await res.json();
      toast.success('Undone', `${data.undone} reverted${data.failed ? `, ${data.failed} failed` : ''}`);
      await loadSessions();
    } catch { toast.error('Error', 'Undo failed'); }
    finally { setUndoingIds(prev => { const s = new Set(prev); logIds.forEach(id => s.delete(id)); return s; }); }
  };

  // Auto-load preview on mount
  useState(() => { handlePreviewMigration(); });

  // ── Columns ─────────────────────────────────────────────────────────────────

  const migrationColumns = [
    ...(!isDone ? [{
      key: 'select', width: '36px',
      header: <input type="checkbox" checked={allFilteredSelected} onChange={toggleSelectAll} />,
      render: row => (
        <input type="checkbox"
          checked={selectedRows.has(row.subscriptionId)}
          onChange={e => {
            setSelectedRows(prev => {
              const s = new Set(prev);
              e.target.checked ? s.add(row.subscriptionId) : s.delete(row.subscriptionId);
              return s;
            });
          }}
          onClick={e => e.stopPropagation()}
        />
      )
    }] : []),
    { key: 'email', header: 'Email', render: row => row.customerEmail },
    { key: 'plan', header: 'Plan', render: row => row.planName },
    { key: 'domain', header: 'Domain', cellStyle: { color: 'var(--admin-text-secondary)' }, render: row => row.domain || '—' },
    { key: 'startDate', header: 'Subscribed', render: row => row.subscriptionCreated
      ? <span style={{ fontSize: '12px', color: 'var(--admin-text-secondary)' }}>{new Date(row.subscriptionCreated * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
      : '—'
    },
    { key: 'interval', header: 'Interval', cellStyle: { color: 'var(--admin-text-secondary)' } },
    { key: 'wave', header: 'Wave', render: row => row.wave === 'previous'
      ? <span style={{ fontSize: '11px', color: 'var(--admin-warning)', fontWeight: '600' }}><i className="fa-solid fa-clock-rotate-left" style={{ marginRight: 4 }} />Previously Migrated</span>
      : row.wave === 'recent'
        ? <span style={{ fontSize: '11px', color: 'var(--admin-info, #3b82f6)', fontWeight: '600' }}><i className="fa-solid fa-star" style={{ marginRight: 4 }} />Recent</span>
        : <span style={{ fontSize: '11px', color: 'var(--admin-text-muted)' }}>—</span>
    },
    { key: 'oldPrice', header: 'Old Price', render: row => (
      <div>
        {row.oldPrice != null
          ? <span style={{ color: 'var(--admin-error)', fontWeight: '700', fontSize: '13px' }}>{(row.currency || 'usd').toUpperCase()} ${Number(row.oldPrice).toFixed(2)}<span style={{ fontWeight: '400', fontSize: '11px', color: 'var(--admin-text-muted)' }}>/{row.interval}</span></span>
          : <code style={{ color: 'var(--admin-error)', fontSize: '11px' }}>{row.oldPriceId}</code>
        }
      </div>
    )},
    { key: 'newPrice', header: 'New Price', render: row => (
      <div>
        {row.newPrice != null
          ? <span style={{ color: 'var(--admin-success)', fontWeight: '700', fontSize: '13px' }}>{(row.currency || 'usd').toUpperCase()} ${Number(row.newPrice).toFixed(2)}<span style={{ fontWeight: '400', fontSize: '11px', color: 'var(--admin-text-muted)' }}>/{row.interval}</span></span>
          : <code style={{ color: 'var(--admin-success)', fontSize: '11px' }}>{row.newPriceId}</code>
        }
      </div>
    )},
    ...(!isDone ? [{
      key: 'sendEmail', header: 'Email', width: '52px',
      render: row => (
        <input type="checkbox"
          checked={rowSendEmail[row.subscriptionId] ?? sendNotifyEmails}
          onChange={e => setRowSendEmail(prev => ({ ...prev, [row.subscriptionId]: e.target.checked }))}
          title="Send price update email when migrating this row"
        />
      )
    }] : []),
    ...(!isDone ? [{
      key: 'migrateOne', header: 'Migrate', width: '90px',
      render: row => (
        <button
          className="admin-btn admin-btn--sm admin-btn--primary"
          onClick={() => handleMigrateSingle(row)}
          disabled={migratingSingle[row.subscriptionId] || migrationRunning}
          title="Migrate this subscription only"
        >
          {migratingSingle[row.subscriptionId] ? '…' : <><i className="fa-solid fa-arrow-right-arrow-left" style={{ marginRight: 4 }} />Migrate</>}
        </button>
      )
    }] : []),
    ...(!isDone ? [{ key: 'pause', header: 'Pause', render: row => (
      <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
        <select className="admin-select" style={{ fontSize: '11px', padding: '3px 6px' }} value={pauseDuration[row.subscriptionId] || ''} onChange={e => setPauseDuration(prev => ({ ...prev, [row.subscriptionId]: e.target.value }))}>
          <option value="">Duration</option>
          {[1,2,3,6].map(m => <option key={m} value={m}>{m}mo</option>)}
        </select>
        <button className="admin-btn admin-btn--sm admin-btn--secondary" onClick={() => pauseDuration[row.subscriptionId] && handlePauseMigration(row.subscriptionId, parseInt(pauseDuration[row.subscriptionId]))} disabled={!pauseDuration[row.subscriptionId] || pausingMigration[row.subscriptionId]}>
          {pausingMigration[row.subscriptionId] ? '...' : 'Pause'}
        </button>
      </div>
    )}] : []),
    { key: 'status', header: isDone ? 'Result' : 'Status', render: row => isDone
      ? row.migrated
        ? <span style={{ color: 'var(--admin-success)', fontWeight: '700' }}><i className="fa-solid fa-check" style={{ marginRight: 4 }} /> Updated</span>
        : <span style={{ color: 'var(--admin-error)' }}><i className="fa-solid fa-xmark" style={{ marginRight: 4 }} /> {row.error || 'Failed'}</span>
      : <span style={{ color: 'var(--admin-warning)', fontSize: '11px' }}>will update</span>
    },
  ];

  const pausedColumns = [
    { key: 'email', header: 'Email', render: row => row.customerEmail },
    { key: 'plan', header: 'Plan', render: row => row.planName },
    { key: 'domain', header: 'Domain', cellStyle: { color: 'var(--admin-text-secondary)' }, render: row => row.domain || '—' },
    { key: 'interval', header: 'Interval', cellStyle: { color: 'var(--admin-text-secondary)' } },
    { key: 'oldPrice', header: 'Current Price', render: row => row.oldPrice != null
      ? <span style={{ color: 'var(--admin-error)', fontWeight: '700', fontSize: '13px' }}>{(row.currency || 'usd').toUpperCase()} ${Number(row.oldPrice).toFixed(2)}<span style={{ fontWeight: '400', fontSize: '11px', color: 'var(--admin-text-muted)' }}>/{row.interval}</span></span>
      : <code style={{ color: 'var(--admin-error)', fontSize: '11px' }}>{row.oldPriceId}</code>
    },
    { key: 'newPrice', header: 'Will Become', render: row => row.newPrice != null
      ? <span style={{ color: 'var(--admin-success)', fontWeight: '700', fontSize: '13px' }}>{(row.currency || 'usd').toUpperCase()} ${Number(row.newPrice).toFixed(2)}<span style={{ fontWeight: '400', fontSize: '11px', color: 'var(--admin-text-muted)' }}>/{row.interval}</span></span>
      : <code style={{ color: 'var(--admin-success)', fontSize: '11px' }}>{row.newPriceId}</code>
    },
    { key: 'pausedUntil', header: 'Paused Until', render: row => <span style={{ color: 'var(--admin-warning)', fontWeight: '600' }}>{new Date(row.pausedUntil * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span> },
    { key: 'migrateOne', header: 'Migrate Now', width: '110px',
      render: row => (
        <button
          className="admin-btn admin-btn--sm admin-btn--primary"
          onClick={() => handleMigrateSingle(row)}
          disabled={migratingSingle[row.subscriptionId]}
          title="Migrate now (ignores pause)"
        >
          {migratingSingle[row.subscriptionId] ? '…' : <><i className="fa-solid fa-arrow-right-arrow-left" style={{ marginRight: 4 }} />Migrate</>}
        </button>
      )
    },
    { key: 'action', header: 'Pause', render: row => <button className="admin-btn admin-btn--sm admin-btn--ghost" onClick={() => handleClearMigrationPause(row.subscriptionId)} disabled={pausingMigration[row.subscriptionId]}>{pausingMigration[row.subscriptionId] ? '...' : 'Resume'}</button> },
  ];

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div>
      <SectionHeader
        title="Price Migration"
        description="Update old subscribers to current pricing. Changes apply at next billing cycle with no immediate charge."
      />

      {/* Tab bar */}
      <div className="admin-tabs" style={{ marginBottom: '16px' }}>
        <button className={`admin-tab${activeTab === 'migrate' ? ' admin-tab--active' : ''}`}
          onClick={() => setActiveTab('migrate')}>Migrate</button>
        <button className={`admin-tab${activeTab === 'history' ? ' admin-tab--active' : ''}`}
          onClick={() => { setActiveTab('history'); loadSessions(); }}>History &amp; Undo</button>
      </div>

      {activeTab === 'migrate' && (
        <>
          {/* Card 1: Migration Preview & Actions */}
          <div className="admin-card" style={{ marginBottom: '24px' }}>
            <h3 className="admin-card-title" style={{ marginBottom: '16px' }}>
              <i className="fa-solid fa-arrows-rotate" style={{ marginRight: 8 }} />
              Subscription Price Updates
            </h3>

            <div className="admin-btn-group" style={{ marginBottom: '16px' }}>
              <button className="admin-btn admin-btn--secondary" onClick={handlePreviewMigration} disabled={migrationRunning}>
                {migrationRunning && !migrationResult ? 'Scanning...' : 'Refresh Preview'}
              </button>
              {migrationPreview && !migrationPreview.error && migrationPreview.total > 0 && (
                <button className="admin-btn admin-btn--primary" onClick={() => setConfirmMigrate(true)} disabled={migrationRunning}>
                  <i className="fa-solid fa-arrow-right-arrow-left" style={{ marginRight: 6 }} />
                  Migrate All {migrationPreview.total} Subscription{migrationPreview.total !== 1 ? 's' : ''}
                </button>
              )}
            </div>

            <label className="admin-checkbox" style={{ marginBottom: '16px', display: 'block' }}>
              <input type="checkbox" checked={sendNotifyEmails} onChange={e => handleGlobalEmailChange(e.target.checked)} />
              Send price update notification email (global default — override per row below)
            </label>

            {migrationPreview?.error && <div className="admin-alert admin-alert--error">{migrationPreview.error}</div>}

            {migrationResult && (
              <div className="admin-alert admin-alert--success" style={{ marginBottom: '16px' }}>
                <p style={{ margin: '0 0 2px 0', fontWeight: '700' }}>Migration complete — {migrationResult.migrated} updated, {migrationResult.failed} failed{migrationResult.gracePaused > 0 ? `, ${migrationResult.gracePaused} deferred` : ''}</p>
                {migrationResult.emailsSent !== null && (
                  <p style={{ margin: 0, fontSize: '12px' }}><i className="fa-solid fa-envelope" style={{ marginRight: 4 }} />{migrationResult.emailsSent} email{migrationResult.emailsSent !== 1 ? 's' : ''} sent</p>
                )}
                {migrationResult.gracePaused > 0 && (
                  <p style={{ margin: '2px 0 0 0', fontSize: '12px', color: 'var(--admin-warning)' }}>
                    <i className="fa-solid fa-clock-rotate-left" style={{ marginRight: 4 }} />
                    {migrationResult.gracePaused} previously migrated subscription{migrationResult.gracePaused !== 1 ? 's' : ''} deferred to grace period — they will migrate when the grace date passes.
                  </p>
                )}
              </div>
            )}

            {/* Wave filter pills */}
            {hasWaves && !isDone && (
              <div className="admin-filter-pills" style={{ marginBottom: '10px' }}>
                <button className={`admin-pill${waveFilter === 'all' ? ' admin-pill--active' : ''}`}
                  onClick={() => setWaveFilter('all')}>
                  All ({rows.length})
                </button>
                <button className={`admin-pill${waveFilter === 'recent' ? ' admin-pill--active' : ''}`}
                  onClick={() => setWaveFilter('recent')}>
                  <i className="fa-solid fa-star" style={{ marginRight: 4 }} />
                  Recent ({recentCount})
                </button>
                <button className={`admin-pill${waveFilter === 'previous' ? ' admin-pill--active' : ''}`}
                  onClick={() => setWaveFilter('previous')}>
                  <i className="fa-solid fa-clock-rotate-left" style={{ marginRight: 4 }} />
                  Previously Migrated ({previousCount})
                </button>
              </div>
            )}

            {/* Grace period input for previously migrated subs */}
            {!isDone && previousCount > 0 && (waveFilter === 'all' || waveFilter === 'previous') && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px', padding: '10px 14px', background: 'var(--admin-elevated)', borderRadius: '8px', flexWrap: 'wrap' }}>
                <i className="fa-solid fa-clock-rotate-left" style={{ color: 'var(--admin-warning)' }} />
                <span style={{ fontSize: '12px', color: 'var(--admin-text-secondary)', flex: 1, minWidth: '200px' }}>
                  <strong style={{ color: 'var(--admin-warning)' }}>Grace period for Previously Migrated ({previousCount}):</strong> set a date until which they are deferred. Leave blank to migrate them immediately.
                </span>
                <input
                  type="date"
                  className="admin-input"
                  style={{ fontSize: '12px', padding: '4px 8px', width: 'auto' }}
                  value={graceUntilDate}
                  min={new Date().toISOString().slice(0, 10)}
                  onChange={e => setGraceUntilDate(e.target.value)}
                />
                {graceUntilDate && (
                  <button className="admin-btn admin-btn--sm admin-btn--ghost" onClick={() => setGraceUntilDate('')}>Clear</button>
                )}
              </div>
            )}

            {/* Service filter pills */}
            {availableServices.length > 2 && (
              <div className="admin-filter-pills" style={{ marginBottom: '10px' }}>
                {availableServices.map(s => (
                  <button key={s}
                    className={`admin-pill${serviceFilter === s ? ' admin-pill--active' : ''}`}
                    onClick={() => { setServiceFilter(s); setPlanFilter('all'); setSelectedRows(new Set()); }}>
                    {s === 'all' ? 'All Services' : s}
                  </button>
                ))}
              </div>
            )}

            {/* Plan sub-filter */}
            {serviceFilter !== 'all' && availablePlans.length > 2 && (
              <div className="admin-filter-pills" style={{ marginBottom: '10px' }}>
                {availablePlans.map(p => (
                  <button key={p}
                    className={`admin-pill${planFilter === p ? ' admin-pill--active' : ''}`}
                    onClick={() => { setPlanFilter(p); setSelectedRows(new Set()); }}>
                    {p === 'all' ? 'All Plans' : p}
                  </button>
                ))}
              </div>
            )}

            {/* Group by price toggle + Migrate Selected bar */}
            {filteredRows.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px', flexWrap: 'wrap', gap: '8px' }}>
                <label className="admin-checkbox" style={{ margin: 0 }}>
                  <input type="checkbox" checked={groupByPrice} onChange={e => setGroupByPrice(e.target.checked)} />
                  Group by price tier
                </label>
                {selectedRows.size > 0 && !isDone && (
                  <div className="admin-btn-group">
                    <span style={{ fontSize: '13px', color: 'var(--admin-text-primary)', padding: '0 4px' }}>{selectedRows.size} selected</span>
                    <button className="admin-btn admin-btn--sm admin-btn--ghost" onClick={() => setSelectedRows(new Set())}>Clear</button>
                    <button className="admin-btn admin-btn--sm admin-btn--primary" onClick={handleMigrateSelected} disabled={migratingSelected}>
                      {migratingSelected ? 'Migrating...' : `Migrate ${selectedRows.size} Selected`}
                    </button>
                  </div>
                )}
              </div>
            )}

            {filteredRows.length > 0 ? (
              groupByPrice && priceGroups ? (
                priceGroups.map(group => {
                  const isCollapsed = collapsedGroups.has(group.key);
                  const groupAllSelected = group.rows.every(r => selectedRows.has(r.subscriptionId));
                  const priceLabel = group.oldPrice != null
                    ? `${(group.currency || 'usd').toUpperCase()} $${Number(group.oldPrice).toFixed(2)} → $${Number(group.newPrice).toFixed(2)}/${group.interval}`
                    : group.key;
                  const urgency = group.newPrice != null && group.oldPrice != null
                    ? group.oldPrice < group.newPrice * 0.75 ? 'high' : group.oldPrice < group.newPrice * 0.95 ? 'medium' : 'low'
                    : 'low';
                  const urgencyColor = urgency === 'high' ? 'var(--admin-error)' : urgency === 'medium' ? 'var(--admin-warning)' : 'var(--admin-text-secondary)';
                  return (
                    <div key={group.key} style={{ marginBottom: '12px', border: '1px solid var(--admin-border)', borderRadius: '8px', overflow: 'hidden' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', background: 'var(--admin-elevated)', cursor: 'pointer' }} onClick={() => toggleGroup(group.key)}>
                        {!isDone && (
                          <input type="checkbox" checked={groupAllSelected} onClick={e => e.stopPropagation()}
                            onChange={e => {
                              setSelectedRows(prev => {
                                const s = new Set(prev);
                                group.rows.forEach(r => e.target.checked ? s.add(r.subscriptionId) : s.delete(r.subscriptionId));
                                return s;
                              });
                            }}
                          />
                        )}
                        <i className="fa-solid fa-tag" style={{ color: urgencyColor, fontSize: '12px' }} />
                        <span style={{ fontWeight: '700', fontSize: '13px', color: urgencyColor }}>{priceLabel}</span>
                        <span style={{ fontSize: '12px', color: 'var(--admin-text-secondary)', marginLeft: '4px' }}>
                          {group.rows.length} subscription{group.rows.length !== 1 ? 's' : ''}
                        </span>
                        {urgency === 'high' && <span style={{ fontSize: '11px', background: 'var(--admin-error)', color: '#fff', padding: '2px 7px', borderRadius: '10px', marginLeft: '4px' }}>Large increase</span>}
                        <i className={`fa-solid fa-chevron-${isCollapsed ? 'down' : 'up'}`} style={{ marginLeft: 'auto', fontSize: '11px', color: 'var(--admin-text-muted)' }} />
                      </div>
                      {!isCollapsed && <DataTable columns={migrationColumns} data={group.rows} />}
                    </div>
                  );
                })
              ) : (
                <DataTable columns={migrationColumns} data={filteredRows} />
              )
            ) : !migrationPreview?.error && !migrationRunning && (
              <p style={{ color: 'var(--admin-success)', fontSize: '13px', margin: '8px 0 0 0' }}>
                <i className="fa-solid fa-circle-check" style={{ marginRight: 6 }} />
                All subscriptions are already on current prices.
              </p>
            )}

            {pausedRows.length > 0 && (
              <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: '1px solid var(--admin-border)' }}>
                <p style={{ margin: '0 0 10px 0', color: 'var(--admin-warning)', fontSize: '13px', fontWeight: '600' }}>
                  <i className="fa-solid fa-pause" style={{ marginRight: 6 }} />
                  {pausedRows.length} subscription{pausedRows.length > 1 ? 's' : ''} paused from migration
                </p>
                <DataTable columns={pausedColumns} data={pausedRows} />
              </div>
            )}
          </div>

          {/* Card 2: Email Notifications */}
          <div className="admin-card">
            <h3 className="admin-card-title" style={{ marginBottom: '6px' }}>
              <i className="fa-solid fa-envelope" style={{ marginRight: 8 }} />
              Price Update Email Notifications
            </h3>
            <p className="admin-card-subtitle" style={{ marginBottom: '16px' }}>
              Sends a "your price is changing" email to all subscribers needing migration, skipping those paused.
            </p>

            <button className="admin-btn admin-btn--secondary" onClick={async () => {
              setNotifyRunning(true); setNotifyResult(null);
              try {
                const res = await apiFetch('/api/admin/notify-price-updates', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ dryRun: true }) });
                setNotifyPreview(await res.json());
              } catch { setNotifyPreview({ error: 'Preview failed' }); }
              finally { setNotifyRunning(false); }
            }} disabled={notifyRunning}>
              {notifyRunning && !notifyResult ? 'Scanning...' : 'Preview Recipients'}
            </button>

            {notifyPreview?.error && <div className="admin-alert admin-alert--error" style={{ marginTop: '16px' }}>{notifyPreview.error}</div>}

            {notifyResult && (
              <div className="admin-alert admin-alert--success" style={{ marginTop: '16px' }}>
                <p style={{ margin: 0, fontWeight: '700' }}>Emails sent — {notifyResult.sent} delivered, {notifyResult.failed} failed, {notifyResult.skipped} paused (skipped)</p>
                {notifyResult.errors?.length > 0 && (
                  <ul style={{ margin: '6px 0 0 0', padding: '0 0 0 16px' }}>
                    {notifyResult.errors.map((e, i) => <li key={i} style={{ color: 'var(--admin-error)', fontSize: '11px' }}>{e.email}: {e.error}</li>)}
                  </ul>
                )}
              </div>
            )}

            {notifyPreview && !notifyPreview.error && (
              <div style={{ marginTop: '16px' }}>
                {notifyPreview.total === 0 ? (
                  <p style={{ color: 'var(--admin-success)', fontSize: '13px', margin: 0 }}>
                    <i className="fa-solid fa-circle-check" style={{ marginRight: 6 }} />
                    No subscribers to notify (all on current prices or paused).
                  </p>
                ) : (
                  <>
                    <div className="admin-flex-between" style={{ marginBottom: '14px', flexWrap: 'wrap', gap: '12px' }}>
                      <p style={{ margin: 0, color: 'var(--admin-text-primary)', fontSize: '13px' }}>
                        <strong style={{ color: 'var(--admin-success)' }}>{notifyPreview.total}</strong> will receive the email
                        {notifyPreview.skipped > 0 && <span style={{ color: 'var(--admin-warning)' }}> · {notifyPreview.skipped} paused (skipped)</span>}
                      </p>
                      <button className="admin-btn admin-btn--primary" onClick={() => setConfirmNotify(true)} disabled={notifyRunning}>
                        Send {notifyPreview.total} Email{notifyPreview.total !== 1 ? 's' : ''}
                      </button>
                    </div>
                    <DataTable
                      columns={[
                        { key: 'email', header: 'Email', render: row => row.customerEmail },
                        { key: 'plan', header: 'Plan', render: row => row.planName },
                        { key: 'domain', header: 'Domain', cellStyle: { color: 'var(--admin-text-secondary)' }, render: row => row.domain || '—' },
                        { key: 'price', header: 'New Price', render: row => <span style={{ color: 'var(--admin-success)', fontWeight: '600' }}>{row.currency} {row.newPrice} / {row.interval}</span> },
                        { key: 'interval', header: 'Interval', cellStyle: { color: 'var(--admin-text-secondary)' } },
                      ]}
                      data={notifyPreview.recipients}
                    />
                  </>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {/* History & Undo tab */}
      {activeTab === 'history' && (
        <div className="admin-card">
          <div className="admin-flex-between" style={{ marginBottom: '12px' }}>
            <h3 className="admin-card-title">Migration History</h3>
            <button className="admin-btn admin-btn--sm admin-btn--secondary" onClick={loadSessions} disabled={sessionsLoading}>Refresh</button>
          </div>
          {sessionsLoading ? (
            <p style={{ color: 'var(--admin-text-secondary)', fontSize: '13px' }}>Loading...</p>
          ) : migrationSessions.length === 0 ? (
            <p style={{ color: 'var(--admin-text-secondary)', fontSize: '13px' }}>No migration history yet.</p>
          ) : migrationSessions.map(session => {
            const activeRows = session.rows.filter(r => !r.undone_at);
            const undoneRows = session.rows.filter(r => r.undone_at);
            const isExpanded = expandedSession === session.session_id;
            const allActiveIds = activeRows.map(r => r.id);
            return (
              <div key={session.session_id} className="admin-card" style={{ marginBottom: '10px', background: 'var(--admin-elevated)' }}>
                <div className="admin-flex-between">
                  <div>
                    <span style={{ fontWeight: '600', fontSize: '13px' }}>
                      {new Date(session.migrated_at).toLocaleString()}
                    </span>
                    <span style={{ marginLeft: '12px', color: 'var(--admin-text-secondary)', fontSize: '12px' }}>
                      {session.rows.length} total · {activeRows.length} active · {undoneRows.length} undone
                    </span>
                  </div>
                  <div className="admin-btn-group">
                    <button className="admin-btn admin-btn--sm admin-btn--ghost"
                      onClick={() => setExpandedSession(isExpanded ? null : session.session_id)}>
                      {isExpanded ? 'Collapse' : 'Expand'}
                    </button>
                    {activeRows.length > 0 && (
                      <button className="admin-btn admin-btn--sm admin-btn--danger"
                        onClick={() => handleUndo(allActiveIds)}
                        disabled={allActiveIds.some(id => undoingIds.has(id))}>
                        Undo All ({activeRows.length})
                      </button>
                    )}
                  </div>
                </div>
                {isExpanded && (
                  <div style={{ marginTop: '12px' }}>
                    <DataTable
                      columns={[
                        { key: 'email', header: 'Email', render: r => r.customer_email },
                        { key: 'plan', header: 'Plan', render: r => r.plan_name },
                        { key: 'domain', header: 'Domain', render: r => r.domain || '—' },
                        { key: 'interval', header: 'Interval', render: r => r.interval },
                        { key: 'old', header: 'Old Price', render: r => <code style={{ color: 'var(--admin-error)', fontSize: '11px' }}>{r.old_price_id}</code> },
                        { key: 'new', header: 'New Price', render: r => <code style={{ color: 'var(--admin-success)', fontSize: '11px' }}>{r.new_price_id}</code> },
                        { key: 'status', header: 'Status', render: r => r.undone_at
                          ? <span style={{ color: 'var(--admin-text-muted)', fontSize: '11px' }}>Undone {new Date(r.undone_at).toLocaleDateString()}</span>
                          : <span style={{ color: 'var(--admin-success)', fontSize: '11px', fontWeight: '600' }}>Active</span>
                        },
                        { key: 'undo', header: '', render: r => !r.undone_at && (
                          <button className="admin-btn admin-btn--sm admin-btn--danger"
                            onClick={() => handleUndo([r.id])}
                            disabled={undoingIds.has(r.id)}>
                            {undoingIds.has(r.id) ? '...' : 'Undo'}
                          </button>
                        )},
                      ]}
                      data={session.rows}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Confirm migrate all dialog */}
      <ConfirmDialog
        open={confirmMigrate}
        onClose={() => setConfirmMigrate(false)}
        onConfirm={handleRunMigration}
        title="Confirm Price Migration"
        message={`This will update ${migrationPreview?.total || 0} subscription(s) to current prices at their next billing cycle. No immediate charges.${sendNotifyEmails ? ' Price update emails will be sent per row settings.' : ''}${graceUntilDate && previousCount > 0 ? ` ${previousCount} previously migrated subscription(s) will be deferred until ${new Date(graceUntilDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}.` : ''}`}
        confirmText="Migrate All"
        loading={migrationRunning}
      />

      {/* Confirm notify dialog */}
      <ConfirmDialog
        open={confirmNotify}
        onClose={() => setConfirmNotify(false)}
        onConfirm={async () => {
          setConfirmNotify(false);
          setNotifyRunning(true); setNotifyResult(null);
          try {
            const res = await apiFetch('/api/admin/notify-price-updates', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ dryRun: false }) });
            const data = await res.json();
            setNotifyResult(data);
            setNotifyPreview(null);
            toast.success('Sent', `${data.sent} email${data.sent !== 1 ? 's' : ''} sent`);
          } catch { setNotifyResult({ error: 'Send failed' }); toast.error('Error', 'Send failed'); }
          finally { setNotifyRunning(false); }
        }}
        title="Send Price Update Emails"
        message={`Send price update emails to ${notifyPreview?.total || 0} subscriber(s)? This cannot be undone.`}
        confirmText="Send Emails"
        loading={notifyRunning}
      />
    </div>
  );
}

export default AdminMigration;
